import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, formatCurrency, SERVICES } from "@/lib/constants";
import { todayIST } from "@/lib/reportPeriods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, FileText, IndianRupee, CreditCard, Download,
  Trash2, Eye, CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";
import { LeadSearchInput } from "@/components/crm/LeadSearchInput";
import { DoctorSelect } from "@/components/crm/DoctorSelect";
import {
  InvoiceLineItems, InvoiceTotals, toInvoiceItems,
} from "@/components/crm/InvoiceLineItems";
import { Field } from "@/pages/Leads";

const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-800",
  partial: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-200 text-gray-500",
};

function InvoiceStatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${STATUS_BADGE[status] || STATUS_BADGE.draft}`}>
      {status}
    </span>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/invoices", { params }).then((r) => setInvoices(r.data.items || [])).catch(() => {});
    api.get("/invoices/stats/summary").then((r) => setStats(r.data)).catch(() => {});
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatBox icon={FileText} label="Total Invoices" value={stats.total_invoices} />
          <StatBox icon={IndianRupee} label="Total Billed" value={formatCurrency(stats.total_billed)} accent="text-navy" />
          <StatBox icon={CheckCircle2} label="Collected" value={formatCurrency(stats.total_collected)} accent="text-green-600" />
          <StatBox icon={AlertTriangle} label="Outstanding" value={formatCurrency(stats.total_due)} accent="text-amber-600" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Invoices</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-navy text-white hover:bg-navy-hover">
          <Plus className="mr-2 h-4 w-4" /> Create Invoice
        </Button>
      </div>

      {/* Invoice list */}
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Invoice #</th>
              <th className="px-4 py-3">Patient</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">No invoices found.</td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} className="border-b last:border-0 hover:bg-nude/30">
                <td className="px-4 py-3 font-semibold text-navy">{inv.invoice_number}</td>
                <td className="px-4 py-3">{inv.patient_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatDateTime(inv.created_at)}</td>
                <td className="px-4 py-3 font-medium">{formatCurrency(inv.grand_total)}</td>
                <td className="px-4 py-3 text-green-600">{formatCurrency(inv.amount_paid)}</td>
                <td className="px-4 py-3 text-amber-600">{formatCurrency(inv.amount_due)}</td>
                <td className="px-4 py-3"><InvoiceStatusBadge status={inv.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewInvoice(inv)} title="View">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {inv.status !== "paid" && inv.status !== "cancelled" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setPaymentOpen(inv)} title="Record payment">
                        <CreditCard className="h-3.5 w-3.5 text-green-600" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => window.open(`${api.defaults.baseURL}/invoices/${inv.id}/pdf`, "_blank")} title="Download PDF">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} onDone={load} />
      <InvoiceDetailDialog invoice={viewInvoice} onClose={() => setViewInvoice(null)} onDone={load} onRecordPayment={(inv) => { setViewInvoice(null); setPaymentOpen(inv); }} />
      <RecordPaymentDialog invoice={paymentOpen} onClose={() => setPaymentOpen(null)} onDone={load} />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <p className={`mt-1 text-lg font-bold ${accent || "text-navy"}`}>{value}</p>
    </div>
  );
}

function CreateInvoiceDialog({ open, onOpenChange, onDone }) {
  const [patientId, setPatientId] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  // Starts EMPTY. A blank row waiting to be filled was the old form telling you
  // to type; the search underneath is the one that knows the prices.
  const [rows, setRows] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIST);
  const [doctorId, setDoctorId] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!patientId) { toast.error("Select a patient"); return; }
    const items = toInvoiceItems(rows);
    if (!items.length) { toast.error("Add at least one procedure"); return; }
    if (invoiceDate > todayIST()) { toast.error("Invoice date cannot be in the future"); return; }
    setSaving(true);
    try {
      await api.post("/invoices", {
        patient_id: patientId,
        items,
        discount: parseFloat(discount) || 0,
        notes,
        due_date: dueDate || null,
        invoice_date: invoiceDate || null,
        doctor_id: doctorId || null,
      });
      toast.success("Invoice created");
      onOpenChange(false);
      setPatientId(""); setPatientSearch(""); setRows([]);
      setDiscount(0); setNotes(""); setDueDate(""); setInvoiceDate(todayIST()); setDoctorId("");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wider than the old form: six columns of billing do not fit in 2xl, and
          the row silently stacks into a column when they do not. */}
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Create Invoice</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Patient *">
            <LeadSearchInput value={patientSearch} onChange={setPatientSearch}
              onLeadSelect={(p) => { setPatientId(p.id); setPatientSearch(p.name); }}
              placeholder="Search patients by name or mobile..."
              searchEndpoint="/patients" />
          </Field>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Line Items</p>
            <InvoiceLineItems rows={rows} onChange={setRows} />
          </div>

          <InvoiceTotals rows={rows} discount={discount} onDiscount={setDiscount} />

          <Field label="Doctor (printed on the invoice and signing it)">
            <DoctorSelect value={doctorId} onChange={setDoctorId} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Invoice Date">
              <Input type="date" value={invoiceDate} max={todayIST()} onChange={(e) => setInvoiceDate(e.target.value)} />
            </Field>
            <Field label="Due Date"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
            <Field label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">{saving ? "Creating..." : "Create Invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDetailDialog({ invoice, onClose, onDone, onRecordPayment }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!invoice) { setDetail(null); return; }
    api.get(`/invoices/${invoice.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [invoice]);

  // Saved on change, not with a form: it is the one thing still editable on a
  // paid invoice, so a bill printed without a doctor can be put right.
  const setDoctor = async (doctorId) => {
    try {
      const { data } = await api.put(`/invoices/${invoice.id}/doctor`, { doctor_id: doctorId || null });
      setDetail((d) => ({ ...(d || invoice), doctor_id: data.doctor_id }));
      toast.success(doctorId ? "Doctor updated" : "Doctor removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!invoice) return null;
  const inv = detail || invoice;

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-navy">Invoice {inv.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{inv.patient_name}</p>
              <p className="text-xs text-muted-foreground">{formatDateTime(inv.created_at)}</p>
            </div>
            <InvoiceStatusBadge status={inv.status} />
          </div>

          {detail && (
            <Field label="Doctor (printed on the invoice and signing it)">
              <DoctorSelect value={detail.doctor_id || ""} onChange={setDoctor} testId="invoice-detail-doctor" />
            </Field>
          )}

          {/* Items */}
          <div className="rounded-lg border border-navy/10">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-cream/50 text-muted-foreground">
                <th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th>
              </tr></thead>
              <tbody>
                {(inv.items || []).map((item, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-3 py-2">{item.description}</td>
                    <td className="px-3 py-2 text-center">{item.quantity}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.rate)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency((item.amount || 0) + (item.tax_amount || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="rounded-lg bg-cream/60 p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatCurrency(inv.subtotal)}</span></div>
            {inv.tax_total > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{formatCurrency(inv.tax_total)}</span></div>}
            {inv.discount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>- {formatCurrency(inv.discount)}</span></div>}
            <div className="mt-1 flex justify-between border-t pt-1 font-bold text-navy"><span>Total</span><span>{formatCurrency(inv.grand_total)}</span></div>
            <div className="flex justify-between text-green-600"><span>Paid</span><span>{formatCurrency(inv.amount_paid)}</span></div>
            <div className="flex justify-between font-semibold text-amber-600"><span>Due</span><span>{formatCurrency(inv.amount_due)}</span></div>
          </div>

          {/* Payments */}
          {detail?.payments?.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payments</p>
              {detail.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg bg-green-50 px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium text-green-700">{formatCurrency(p.amount)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{p.method} · {formatDateTime(p.created_at)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.received_by_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/invoices/${inv.id}/pdf`, "_blank")}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
          </Button>
          {inv.status !== "paid" && inv.status !== "cancelled" && (
            <Button onClick={() => onRecordPayment(inv)} className="bg-green-700 text-white hover:bg-green-800">
              <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Record Payment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentDialog({ invoice, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) setAmount(invoice.amount_due || "");
  }, [invoice]);

  if (!invoice) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/payments", { invoice_id: invoice.id, amount: amt, method, reference });
      toast.success("Payment recorded");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Record Payment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 p-3">
            <p className="text-sm font-semibold">{invoice.invoice_number} · {invoice.patient_name}</p>
            <p className="text-xs text-muted-foreground">Due: {formatCurrency(invoice.amount_due)}</p>
          </div>
          <Field label="Amount (₹)">
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          </Field>
          <Field label="Payment Method">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reference (optional)">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction ID / Cheque no." />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-green-700 text-white hover:bg-green-800">
            {saving ? "Recording..." : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
